import { useEffect, useState, useMemo } from "react";
import { Inbox, RefreshCw, Mail, Search, ChevronRight, Tag, AlertCircle, CheckCircle2, Loader2, Send, FileText } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const CAT_STYLES = {
  VERBALI:             { color: "#ff0033", bg: "#ff003322", label: "Verbale", icon: "🚓" },
  BOLLO:               { color: "#f5a623", bg: "#f5a62322", label: "Bollo",  icon: "🧾" },
  AGENZIA_RISCOSSIONE: { color: "#9c27b0", bg: "#9c27b022", label: "AdE-R", icon: "🏛️" },
  SINISTRI:            { color: "#2196f3", bg: "#2196f322", label: "Sinistro", icon: "💥" },
  GENERICO:            { color: "#888", bg: "#88888822", label: "Generico", icon: "📄" },
  RICEVUTA_PEC:        { color: "#4caf50", bg: "#4caf5022", label: "Ricevuta PEC", icon: "✉️" },
};

export default function PecInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState({ total: 0, by_categoria: {}, state: {} });
  const [connTest, setConnTest] = useState(null);

  const load = async () => {
    try {
      const [list, st] = await Promise.all([
        api.get("/pec-inbox", { params: { categoria: filterCat || undefined, limit: 200 } }),
        api.get("/pec-inbox/stats/summary"),
      ]);
      setItems(list.data.items || []);
      setStats(st.data);
    } catch (e) {
      toast.error("Errore caricamento PEC: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterCat]);

  function poll() {
    setPolling(true);
    api.post("/pec-inbox/poll")
      .then((r) => {
        toast.success(`Scaricati ${r.data.fetched} messaggi, ${r.data.saved} nuovi, ${r.data.verbali_processed} verbali auto-processati`);
        return load();
      })
      .catch((e) => {
        const msg = e?.response?.data?.detail || e.message;
        toast.error("Poll fallito: " + msg);
      })
      .finally(() => setPolling(false));
  }

  const testConnection = async () => {
    setConnTest({ loading: true });
    try {
      const r = await api.post("/pec-inbox/test-connection");
      setConnTest(r.data);
      if (r.data.ok) toast.success("Connessione IMAP OK");
      else toast.error("Connessione fallita: " + r.data.error);
    } catch (e) { setConnTest({ ok: false, error: e.message }); }
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return items;
    return items.filter(it =>
      (it.subject || "").toLowerCase().includes(s) ||
      (it.from_address || "").toLowerCase().includes(s) ||
      (it.categoria || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/pec-inbox/${id}`);
      setSelected(r.data.item);
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const downloadAttachment = async (pecId, idx, filename) => {
    try {
      const r = await api.get(`/pec-inbox/${pecId}/attachment/${idx}`);
      const att = r.data.attachment;
      const a = document.createElement("a");
      a.href = `data:${att.content_type};base64,${att.data_b64}`;
      a.download = att.filename || filename || `att_${idx}`;
      a.click();
    } catch (e) { toast.error("Errore download: " + e.message); }
  };

  const reclassify = async (id, cat) => {
    try {
      await api.post(`/pec-inbox/${id}/reclassify`, { categoria: cat });
      toast.success("Riclassificato come " + cat);
      await load();
      if (selected?.id === id) await openDetail(id);
    } catch (e) { toast.error("Errore: " + e.message); }
  };

  const createRinotifica = async (pecId) => {
    try {
      const r = await api.post(`/rinotifiche/from-pec/${pecId}`);
      toast.success(`Rinotifica creata in stato ${r.data.rinotifica?.stato}`);
      window.location.href = `/admin/rinotifiche`;
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-6" data-testid="pec-inbox-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Inbox className="text-red-500" /> PEC Inbox AI
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Monitoraggio casella PEC · Classificazione automatica documenti · Auto-rinotifica verbali
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              try {
                const r = await api.post("/pec-inbox/process-pending-verbali");
                toast.success(r.data.message);
                setTimeout(load, 2000);
              } catch (e) { toast.error("Errore: " + e.message); }
            }}
            className="px-3 py-2 bg-amber-700 hover:bg-amber-600 rounded-lg text-sm flex items-center gap-2 border border-amber-600"
            data-testid="process-pending-btn"
            title="Processa tutti i VERBALI in inbox che non hanno ancora una rinotifica associata"
          >
            ⚖️ Processa verbali pendenti
          </button>
          <button
            onClick={testConnection}
            data-testid="pec-test-connection"
            className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2 border border-zinc-700"
          >
            <Mail size={16}/> Test connessione
          </button>
          <button
            onClick={poll}
            disabled={polling}
            data-testid="pec-poll-btn"
            className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-sm flex items-center gap-2 font-semibold disabled:opacity-50"
          >
            {polling ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
            {polling ? "Scaricamento in corso..." : "Scarica nuovi messaggi"}
          </button>
        </div>
      </div>

      {/* Connection test result */}
      {connTest && !connTest.loading && (
        <div className={`p-3 rounded-lg border text-sm ${connTest.ok ? "bg-green-900/30 border-green-600" : "bg-red-900/30 border-red-600"}`} data-testid="pec-conn-result">
          {connTest.ok ? (
            <span className="flex items-center gap-2"><CheckCircle2 size={16}/> Connessione IMAP OK su <b>{connTest.host}</b> con <b>{connTest.user}</b></span>
          ) : (
            <span className="flex items-center gap-2"><AlertCircle size={16}/> <b>Errore IMAP:</b> {connTest.error}</span>
          )}
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <button
          onClick={() => setFilterCat("")}
          className={`p-3 rounded-lg border text-left ${!filterCat ? "border-red-500 bg-red-900/20" : "border-zinc-800 bg-zinc-900"}`}
          data-testid="filter-all"
        >
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs text-zinc-400">Totale messaggi</div>
        </button>
        {Object.entries(CAT_STYLES).filter(([k]) => k !== "RICEVUTA_PEC").map(([cat, s]) => (
          <button
            key={cat}
            onClick={() => setFilterCat(cat)}
            className={`p-3 rounded-lg border text-left transition ${filterCat === cat ? "border-white bg-zinc-800" : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"}`}
            data-testid={`filter-${cat.toLowerCase()}`}
          >
            <div className="text-2xl font-bold" style={{ color: s.color }}>
              {stats.by_categoria?.[cat] || 0}
            </div>
            <div className="text-xs text-zinc-400 flex items-center gap-1">
              <span>{s.icon}</span> {s.label}
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16}/>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per oggetto, mittente, categoria…"
          className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm focus:outline-none focus:border-red-500"
          data-testid="pec-search-input"
        />
      </div>

      {/* List + Detail layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* List */}
        <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="pec-list">
          {loading && <div className="text-zinc-500 text-center py-8"><Loader2 className="animate-spin inline mr-2"/> Caricamento…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-zinc-500 text-center py-12 border border-dashed border-zinc-800 rounded-lg">
              <Inbox className="mx-auto mb-2 text-zinc-700" size={32}/>
              <div>Nessun messaggio PEC.</div>
              <div className="text-xs mt-1">Clicca &quot;Scarica nuovi messaggi&quot; per popolare.</div>
            </div>
          )}
          {filtered.map(it => {
            const s = CAT_STYLES[it.categoria] || CAT_STYLES.GENERICO;
            const isActive = selected?.id === it.id;
            return (
              <button
                key={it.id}
                onClick={() => openDetail(it.id)}
                data-testid={`pec-item-${it.id}`}
                className={`w-full text-left p-3 rounded-lg border transition ${isActive ? "border-red-500 bg-zinc-800" : "border-zinc-800 bg-zinc-900 hover:border-zinc-600"}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ color: s.color, background: s.bg }}>
                    {s.icon} {s.label}
                  </span>
                  <span className="text-[10px] text-zinc-500">{(it.date || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-sm font-medium truncate">{it.subject || "(senza oggetto)"}</div>
                <div className="text-xs text-zinc-400 truncate">{it.from_address}</div>
                {it.attachments?.length > 0 && (
                  <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1">
                    <FileText size={11}/> {it.attachments.length} allegat{it.attachments.length === 1 ? "o" : "i"}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg p-5 max-h-[70vh] overflow-y-auto" data-testid="pec-detail">
          {!selected && (
            <div className="text-zinc-500 text-center py-16">
              <Mail className="mx-auto mb-3 text-zinc-700" size={48}/>
              <div>Seleziona un messaggio per vedere i dettagli</div>
            </div>
          )}
          {selected && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b border-zinc-800">
                <div>
                  <div className="text-xl font-bold">{selected.subject || "(senza oggetto)"}</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Da: <b>{selected.from_address}</b> · A: {selected.to_address} · {selected.date}
                  </div>
                </div>
                <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{
                  color: (CAT_STYLES[selected.categoria]||CAT_STYLES.GENERICO).color,
                  background: (CAT_STYLES[selected.categoria]||CAT_STYLES.GENERICO).bg
                }}>
                  {(CAT_STYLES[selected.categoria]||CAT_STYLES.GENERICO).icon} {(CAT_STYLES[selected.categoria]||CAT_STYLES.GENERICO).label}
                </span>
              </div>

              {/* Classificazione */}
              {selected.classification && (
                <div className="text-xs text-zinc-400 bg-zinc-950 p-3 rounded border border-zinc-800">
                  <b>Classificazione AI:</b> {selected.classification.categoria}
                  {selected.classification.confidence && <> · Confidence: {(selected.classification.confidence*100).toFixed(0)}%</>}
                  {selected.classification.motivo && <> · {selected.classification.motivo}</>}
                  <div className="text-[10px] text-zinc-500 mt-1">method: {selected.classification.method}</div>
                </div>
              )}

              {/* Override categoria */}
              <div>
                <div className="text-xs text-zinc-500 mb-1">Riclassifica manualmente:</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(CAT_STYLES).filter(([k]) => k !== "RICEVUTA_PEC").map(([k, s]) => (
                    <button key={k} onClick={() => reclassify(selected.id, k)}
                      data-testid={`reclassify-${k.toLowerCase()}`}
                      className="px-2 py-1 text-xs rounded border border-zinc-700 hover:border-white"
                      style={{ color: s.color }}>
                      {s.icon} {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Azioni */}
              {selected.categoria === "VERBALI" && (
                <div className="bg-red-900/20 border border-red-700 rounded-lg p-3">
                  <div className="text-sm font-semibold flex items-center gap-2 mb-2">
                    <AlertCircle size={16}/> Documento riconosciuto come VERBALE
                  </div>
                  {selected.rinotifica_id ? (
                    <a href={`/admin/rinotifiche?id=${selected.rinotifica_id}`}
                       className="text-xs text-red-300 underline" data-testid="goto-rinotifica">
                      → Vai alla pratica rinotifica
                    </a>
                  ) : (
                    <button onClick={() => createRinotifica(selected.id)}
                      data-testid="create-rinotifica-btn"
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-500 rounded text-sm font-semibold flex items-center gap-2">
                      <Send size={14}/> Avvia procedura rinotifica
                    </button>
                  )}
                </div>
              )}

              {/* Allegati */}
              {selected.attachments?.length > 0 && (
                <div>
                  <div className="text-xs text-zinc-500 mb-2 font-semibold">Allegati ({selected.attachments.length})</div>
                  <div className="space-y-1">
                    {selected.attachments.map((att, idx) => (
                      <button key={idx} onClick={() => downloadAttachment(selected.id, idx, att.filename)}
                        data-testid={`pec-att-${idx}`}
                        className="w-full flex items-center justify-between text-left p-2 bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 rounded text-sm">
                        <span className="flex items-center gap-2">
                          <FileText size={14}/> {att.filename}
                          <span className="text-zinc-500 text-xs">({(att.size/1024).toFixed(1)} KB)</span>
                        </span>
                        <ChevronRight size={14} className="text-zinc-500"/>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Body */}
              {selected.text && (
                <div>
                  <div className="text-xs text-zinc-500 mb-1 font-semibold">Corpo messaggio</div>
                  <pre className="text-xs text-zinc-300 bg-zinc-950 p-3 rounded border border-zinc-800 whitespace-pre-wrap max-h-60 overflow-y-auto">
{selected.text.slice(0, 4000)}{selected.text.length > 4000 ? "\n…" : ""}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
